import * as React from "react";
import { graphql, Script } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";

import Layout from "../components/layout";
import Sidebar from "../components/organisms/sidebar/sidebar";
import SnsShareButton from "../components/molecules/block/sns-share";
import Breadcrumbs from "../components/molecules/navigation/breadcrumbs";
import TableOfIndex from "../components/molecules/navigation/table-of-index";
import Cta from "../components/molecules/block/cta";
import ArticleCardRelated from "../components/molecules/block/article-card-related";
import AuthorProfile from "../components/molecules/block/author-profile";
import IntervieweeProfile from "../components/molecules/block/interviewee-profile";
import ContainerMainWithSidebar from "../components/atoms/layout/container-main-with-sidebar";
import ContentArticleBody from "../components/atoms/layout/content-article-body";
import ContentEditor from "../components/atoms/layout/content-editor";
import HeadingLv1 from "../components/atoms/typography/heading-lv1";
import HeadingRelated from "../components/atoms/typography/heading-related";
import TagsLists from "../components/atoms/tag/tags-lists";
import Read from "../components/atoms/time/read";
import Post from "../components/atoms/time/post";
import Horizon from "../components/atoms/list/horizon";

// NOTE : graphQLを使ってContentfulのContent model「StoriesArticle」にデータを問い合わせる。
export const query = graphql`
  query ($id: String!) {
    contentfulStoriesArticle(id: { eq: $id }) {
      id
      title
      publishDateJP: publishDate(formatString: "YYYY/MM/DD")
      publishDate
      updatedAtJP: updatedAt(formatString: "YYYY/MM/DD")
      updatedAt
      description
      slug
      authorProfile {
        authorName
        avatar {
          gatsbyImageData
          title
        }
        position
        introduction {
          childMarkdownRemark {
            html
          }
        }
      }
      content {
        childMarkdownRemark {
          html
          tableOfContents(absolute: false, maxDepth: 3)
        }
      }
      tags {
        tagName
        slug
        id
      }
      eyeCatch {
        gatsbyImageData
        title
        file {
          url
        }
        height
        width
      }
      leadText {
        childMarkdownRemark {
          html
        }
      }
      readTime
      relatedArticle {
        ... on ContentfulColumnsArticle {
          title
          eyeCatch {
            gatsbyImageData
          }
          publishDateJP: publishDate(formatString: "YYYY/MM/DD")
          publishDate
          updatedAtJP: updatedAt(formatString: "YYYY/MM/DD")
          updatedAt
          slug
          tags {
            tagName
            id
          }
          id
        }
      }
      callToAction {
        buttonLinkLabel
        buttonLinkUrl
        ctaTitle
        ctaContent {
          childMarkdownRemark {
            html
          }
        }
      }
      interviewer {
        authorName
        avatar {
          gatsbyImageData
          title
        }
        introduction {
          childMarkdownRemark {
            html
          }
        }
        position
      }
      interviewee {
        authorName
        avatar {
          gatsbyImageData
          title
        }
        introduction {
          childMarkdownRemark {
            html
          }
        }
        position
        id
      }
    }
  }
`;

/**
 * @type {Object} 「ストーリーズ 記事（times/stories/***）」DOMです。
 * @return {Object} body要素のDOMをすべてを返します。
 * @param {Array} data 上記で定義されているgraphQLのquery結果が入ります。
 *
 */

const StoriesArticle = ({ data }) => {
  return (
    <Layout>
      {data.contentfulStoriesArticle.title !== null && (
        <Breadcrumbs
          items={[
            { id: "layer1", url: "/", text: "ホーム" },
            { id: "layer2", url: "/times/", text: "BAsixsタイムズ" },
            { id: "layer3", url: "/times/stories/", text: "ストーリーズ" },
          ]}
          currentPageTitle={data.contentfulStoriesArticle.title}
        />
      )}

      <ContainerMainWithSidebar>
        <main>
          <article>
            <header>
              {data.contentfulStoriesArticle.title !== null && (
                <HeadingLv1 text={data.contentfulStoriesArticle.title} />
              )}
              {data.contentfulStoriesArticle.tags !== null && (
                <TagsLists>
                  {data.contentfulStoriesArticle.tags.map(
                    ({ tagName, slug, id }) => {
                      return (
                        <li key={id}>
                          <a href={`/tags/${slug}/`}>{tagName}</a>
                        </li>
                      );
                    }
                  )}
                </TagsLists>
              )}
              <Read number={data.contentfulStoriesArticle.readTime} />
              <Post
                publishDateJP={data.contentfulStoriesArticle.publishDateJP}
                publishDate={data.contentfulStoriesArticle.publishDate}
                updatedAt={data.contentfulStoriesArticle.updatedAt}
                updatedAtJP={data.contentfulStoriesArticle.updatedAtJP}
              />
            </header>
            <ContentArticleBody>
              {data.contentfulStoriesArticle.authorProfile !== null && (
                <AuthorProfile title="この記事を書いた人">
                  <figure>
                    <GatsbyImage
                      image={
                        data.contentfulStoriesArticle.authorProfile.avatar
                          .gatsbyImageData
                      }
                      alt={
                        data.contentfulStoriesArticle.authorProfile.avatar.title
                      }
                      layout="constrained"
                    />
                  </figure>
                  <div>
                    <dl>
                      <dt>
                        <cite>
                          {
                            data.contentfulStoriesArticle.authorProfile
                              .authorName
                          }
                        </cite>
                        {data.contentfulStoriesArticle.authorProfile
                          .position !== null && (
                          <span>
                            {
                              data.contentfulStoriesArticle.authorProfile
                                .position
                            }
                          </span>
                        )}
                      </dt>
                      <dd
                        dangerouslySetInnerHTML={{
                          __html:
                            data.contentfulStoriesArticle.authorProfile
                              .introduction.childMarkdownRemark.html,
                        }}
                      />
                    </dl>
                  </div>
                </AuthorProfile>
              )}

              {data.contentfulStoriesArticle.leadText !== null && (
                <ContentEditor>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        data.contentfulStoriesArticle.leadText
                          .childMarkdownRemark.html,
                    }}
                  />
                </ContentEditor>
              )}

              {data.contentfulStoriesArticle.content !== null && (
                <TableOfIndex>
                  <nav
                    dangerouslySetInnerHTML={{
                      __html:
                        data.contentfulStoriesArticle.content
                          .childMarkdownRemark.tableOfContents,
                    }}
                    aria-label="Table Of Index"
                  />
                </TableOfIndex>
              )}

              {data.contentfulStoriesArticle.eyeCatch !== null && (
                <figure>
                  <GatsbyImage
                    image={
                      data.contentfulStoriesArticle.eyeCatch.gatsbyImageData
                    }
                    alt={data.contentfulStoriesArticle.eyeCatch.title}
                    layout="constrained"
                    style={{ marginTop: "3.2rem" }}
                  />
                </figure>
              )}

              {data.contentfulStoriesArticle.interviewer !== null && (
                <AuthorProfile title="インタビューした人">
                  <figure>
                    <GatsbyImage
                      image={
                        data.contentfulStoriesArticle.interviewer.avatar
                          .gatsbyImageData
                      }
                      alt={
                        data.contentfulStoriesArticle.interviewer.avatar.title
                      }
                      layout="constrained"
                    />
                  </figure>
                  <div>
                    <dl>
                      <dt>
                        <cite>
                          {data.contentfulStoriesArticle.interviewer.authorName}
                        </cite>
                        <span>
                          {data.contentfulStoriesArticle.interviewer.position}
                        </span>
                      </dt>
                      <dd
                        dangerouslySetInnerHTML={{
                          __html:
                            data.contentfulStoriesArticle.interviewer
                              .introduction.childMarkdownRemark.html,
                        }}
                      />
                    </dl>
                  </div>
                </AuthorProfile>
              )}

              {data.contentfulStoriesArticle.interviewee !== null && (
                <IntervieweeProfile>
                  <ul>
                    {data.contentfulStoriesArticle.interviewee.map(
                      ({ id, avatar, authorName, position, introduction }) => {
                        return (
                          <li key={id}>
                            <div>
                              <figure>
                                <GatsbyImage
                                  image={avatar.gatsbyImageData}
                                  alt={avatar.title}
                                  layout="constrained"
                                />
                              </figure>
                              <div>
                                <dl>
                                  <dt>
                                    <cite>{authorName}</cite>
                                    <span>{position}</span>
                                  </dt>
                                  <dd
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        introduction.childMarkdownRemark.html,
                                    }}
                                  />
                                </dl>
                              </div>
                            </div>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </IntervieweeProfile>
              )}

              {data.contentfulStoriesArticle.content !== null && (
                <ContentEditor>
                  <div
                    dangerouslySetInnerHTML={{
                      __html:
                        data.contentfulStoriesArticle.content
                          .childMarkdownRemark.html,
                    }}
                  />
                </ContentEditor>
              )}
            </ContentArticleBody>
            <footer>
              {data.contentfulStoriesArticle.callToAction !== null && (
                <Cta
                  title={data.contentfulStoriesArticle.callToAction.ctaTitle}
                  url={data.contentfulStoriesArticle.callToAction.buttonLinkUrl}
                  label={
                    data.contentfulStoriesArticle.callToAction.buttonLinkLabel
                  }
                >
                  {data.contentfulStoriesArticle.callToAction.ctaContent !==
                    null && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          data.contentfulStoriesArticle.callToAction.ctaContent
                            .childMarkdownRemark.html,
                      }}
                    />
                  )}
                </Cta>
              )}

              {data.contentfulStoriesArticle.relatedArticle !== null && (
                <section>
                  <>
                    <HeadingRelated text="こちらの記事も合わせて読む" />
                    <Horizon>
                      {data.contentfulStoriesArticle.relatedArticle.map(
                        ({
                          title,
                          eyeCatch,
                          publishDate,
                          publishDateJP,
                          updatedAtJP,
                          updatedAt,
                          slug,
                          tags,
                          id,
                        }) => {
                          return (
                            <li key={id}>
                              <ArticleCardRelated
                                url={`/times/columns/${slug}/`}
                                title={title}
                                tags={tags}
                                publishDate={publishDate}
                                publishDateJP={publishDateJP}
                                updatedAt={updatedAt}
                                updatedAtJP={updatedAtJP}
                              >
                                <header>
                                  <figure>
                                    {eyeCatch !== null && (
                                      <GatsbyImage
                                        image={eyeCatch.gatsbyImageData}
                                        alt=""
                                        layout="constrained"
                                      />
                                    )}
                                  </figure>
                                </header>
                              </ArticleCardRelated>
                            </li>
                          );
                        }
                      )}
                    </Horizon>
                  </>
                </section>
              )}

              {data.contentfulStoriesArticle.title !== null &&
                data.contentfulStoriesArticle.slug !== null && (
                  <SnsShareButton
                    title={`${data.contentfulStoriesArticle.title}`}
                    url={`https://basixs.com/times/stories/${data.contentfulStoriesArticle.slug}/`}
                  />
                )}
            </footer>
          </article>
        </main>
        <Sidebar />
      </ContainerMainWithSidebar>
    </Layout>
  );
};
export default StoriesArticle;

/**
 * @type {Object} head要素のDOMです。
 * @param {Array} data 上記で定義されているgraphQLのquery結果が入ります。
 *
 */

export const Head = ({ data }) => (
  <>
    <html lang="ja" />
    {data.contentfulStoriesArticle.title !== null && (
      <title>
        {`${data.contentfulStoriesArticle.title} | BAsixs（ベーシックス）`}
      </title>
    )}
    {data.contentfulStoriesArticle.description !== null && (
      <meta
        name="description"
        content={data.contentfulStoriesArticle.description}
      />
    )}
    <meta name="format-detection" content="telephone=no" />
    <meta name="twitter:card" content="summary_large_image" />
    {data.contentfulStoriesArticle.title !== null && (
      <meta
        property="og:title"
        content={`${data.contentfulStoriesArticle.title} | BAsixs（ベーシックス）`}
      />
    )}
    <meta property="og:type" content="article" />
    {data.contentfulStoriesArticle.slug !== null && (
      <meta
        property="og:url"
        content={`https://basixs.com/times/stories/${data.contentfulStoriesArticle.slug}/`}
      />
    )}
    {data.contentfulStoriesArticle.description !== null && (
      <meta
        property="og:description"
        content={data.contentfulStoriesArticle.description}
      />
    )}
    {data.contentfulStoriesArticle.eyeCatch !== null && (
      <>
        <meta
          property="og:image"
          content={`https:${data.contentfulStoriesArticle.eyeCatch.file.url}`}
        />
        <meta
          property="og:image:width"
          content={data.contentfulStoriesArticle.eyeCatch.height}
        />
        <meta
          property="og:image:height"
          content={data.contentfulStoriesArticle.eyeCatch.width}
        />
      </>
    )}
    {data.contentfulStoriesArticle.slug !== null && (
      <link
        rel="canonical"
        href={`https://basixs.com/times/stories/${data.contentfulStoriesArticle.slug}/`}
      />
    )}

    {/* NOTE: Embedlyをこのページにインストールしています。（ref.https://docs.embed.ly/reference/platformjs） */}
    <Script id="embedly" src="https://cdn.embedly.com/widgets/platform.js" />

    {/* NOTE: Embedlyがデフォルトで適用するStyleをこのStyleで強引に変更しています。（可能な範囲でBAsixsのトンマナに寄せています。） */}
    <style className="embedly-css">
      {`
        body#cards {
          padding: 0;
        }

        .embedly-card {
          margin-top: 3.2rem;
        }

        .card.article {
          border: solid 0.1rem #EAEAEA;
          padding: 1.6rem;
        }

        .card .txt-bd {
          padding: 0;
        }

        @media (min-width: 450px) {
          .card.sm-img .pair-bd>.txt-bd {
            padding-left: 1.6rem;
          }
        }

        .card.sm-img .art-bd {
          padding-bottom: 1.6rem
        }
        @media (min-width: 450px) {
          .card.sm-img .art-bd {
            padding-bottom: 0;
          }
        }

        .card .art-bd-img {
          border: solid 0.1rem #EAEAEA;
        }

        .hdr {
          display: none;
        }

        .card.video {
          border: solid 0.1rem #EAEAEA;
          padding: 0;
        }
      `}
    </style>
  </>
);
